/**
 * LX-04 Lexical variation (REFIT-S, DESIGN CD-119 and ED-168; docs/plan/REFIT-S-DESIGN.md §2; the candidate G4 of CH8-1,
 * docs/reports/ch8-short-candidates.md §1).
 *
 * The estimate is the moving-average type-token ratio over a 50-token window (MATTR-50) of the document's CONTENT tokens:
 * the folded WORD tokens of the prose-register sentences that are not structural units, in document order, minus the
 * function words of `lang/en/v3/function-words.json`. It is a level, not a rate: there is no shrinkage and no interval;
 * `est` is the MATTR itself (the per-channel calibration map is the calibrated reading). Higher diversity is machine-like
 * in short texts (CH8-1: short human 0.838, machine 0.923), so direction +1.
 *
 * Scope. The channel is scored only from 150 to 999 prose words, which is the calibration's own B1-B2 bounds
 * (`lengthGroupOf`): below 150 it abstains, and at 1,000 prose words and over it abstains because long human fiction
 * varies its vocabulary as much as machine text and the direction reverses at 10,000 words. Below 50 content tokens there
 * is no full window, so it abstains too (CH8 used the plain TTR there; here the model term is zero instead).
 *
 * Evidence. Each prose paragraph with at least 50 content tokens gets its own MATTR-50 (the "local MATTR"); the evidence
 * spans are the up-to-3 paragraphs with the highest local MATTR, weighted by it. There is no per-paragraph event series, so
 * no change-point segments and no FDR test for a level: `nElevated` and `nHotspot` stay 0 and the flags are withheld (as
 * under ED-13). The channel is outside `LEGACY_CHANNEL_IDS` and the seed-rules composite (`SHORT_TEXT_CHANNEL_IDS`).
 *
 * `mattr` is the operation-for-operation twin of `tools/ch8/features.ts`'s `mattr`, so the engine and the CH8 features agree
 * to the last bit (checked by `tools/refit/lx04-parity.ts`).
 */
import { REGISTER_PROSE, SENT_STRUCTURAL, TOK_WORD } from '../document/types.ts';
import FUNCTION_WORDS from '../lang/en/v3/function-words.json' with { type: 'json' };
import { abstainResult, languageGate, makeEvidence, scoredResult } from './framework.ts';
import { fmt } from './helpers.ts';
import type { ChannelDef, ChannelImpl, ChannelResult, Ramp, RunContext } from './types.ts';

const FN = new Set<string>((FUNCTION_WORDS as { words: string[] }).words);

export const MATTR_WINDOW = 50;
/** Prose-word range in which the channel is scored: the calibration's B1-B2 group. */
export const LX04_MIN_PROSE_WORDS = 150;
export const LX04_MAX_PROSE_WORDS = 1000;
/** Content tokens a paragraph needs for its own local MATTR (one full window). */
export const LX04_MIN_LOCAL_TOKENS = MATTR_WINDOW;
/** The data-weight scale: κ = E/(E + 100), display only. */
export const LX04_KAPPA_M = 100;

export const LX04_REASON_SHORT = 'fewer than 150 prose words';
export const LX04_REASON_LONG =
	'not scored at 1,000 prose words and over: lexical variation separates machine from human text only in short texts (CH8-1); long human fiction varies its vocabulary as much as machine text, and the direction reverses at 10,000 words';
export const LX04_FLAGS_WITHHELD =
	'no hotspot test for a level: the local MATTR of a paragraph is shown as evidence only (DESIGN ED-168)';
export const LX04_REASON_TOKENS = 'fewer than 50 content tokens (no full window)';

const RAMP: Ramp = [
	[0.8, 8],
	[0.84, 20],
	[0.88, 40],
	[0.92, 65],
	[0.95, 85],
	[0.98, 95],
];

export const lx04Def: ChannelDef = {
	id: 'LX-04',
	name: 'Lexical variation',
	family: 'VOCAB',
	axis: 'authorship',
	registers: ['prose'],
	direction: 1,
	oneWay: true,
	estimator: 'distributional',
	features: [
		{ id: 'mattr50', label: 'Moving-average type-token ratio of content words (50-token window)' },
	],
	minExposure: LX04_MIN_LOCAL_TOKENS,
	exposureUnit: 'words',
	lang: 'native',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'MATTR-50 of content words',
	// mu: CH8-1's short human mean; m: the κ scale (κ = E/(E + m)); phi0 1 (no overdispersion model for a level)
	prior: { mu: 0.838, m: LX04_KAPPA_M, phi0: 1 },
	// the channel is outside the seed composite (SHORT_TEXT_CHANNEL_IDS); the constants only fill the model's channel block
	weight: 1,
	nd: 0.15,
	changes: [],
};

/** Moving-average type-token ratio over a fixed window; the plain TTR (and `short` true) below one window. */
export function mattr(
	tokens: readonly string[],
	w = MATTR_WINDOW
): { value: number | null; short: boolean } {
	const n = tokens.length;
	if (n === 0) return { value: null, short: true };
	if (n < w) return { value: new Set(tokens).size / n, short: true };
	const freq = new Map<string, number>();
	let types = 0;
	for (let i = 0; i < w; i++) {
		const t = tokens[i] as string;
		const c = freq.get(t) ?? 0;
		if (c === 0) types++;
		freq.set(t, c + 1);
	}
	let total = types;
	for (let i = w; i < n; i++) {
		const out = tokens[i - w] as string;
		const co = (freq.get(out) as number) - 1;
		freq.set(out, co);
		if (co === 0) types--;
		const t = tokens[i] as string;
		const c = freq.get(t) ?? 0;
		if (c === 0) types++;
		freq.set(t, c + 1);
		total += types;
	}
	return { value: total / ((n - w + 1) * w), short: false };
}

export interface ContentParagraph {
	/** Paragraph index. */
	p: number;
	/** Range in the document's content-token list. */
	from: number;
	to: number;
	/** Character range of the paragraph's included sentences (first start, last end). */
	charStart: number;
	charEnd: number;
}

export interface ContentTokens {
	tokens: string[];
	/** The paragraphs that contributed content tokens, in document order. */
	paragraphs: ContentParagraph[];
}

/**
 * The content tokens of a document (CH8-1 §1): folded WORD tokens of prose-register sentences that are not structural
 * units, in document order, minus the function words; with the per-paragraph ranges.
 */
export function contentTokens(doc: RunContext['doc']): ContentTokens {
	const { sentences: S, tokens: T } = doc;
	const tokens: string[] = [];
	const paragraphs: ContentParagraph[] = [];
	let cur: ContentParagraph | null = null;
	for (let s = 0; s < S.count; s++) {
		if (S.register[s] !== REGISTER_PROSE || ((S.flags[s] as number) & SENT_STRUCTURAL) !== 0)
			continue;
		if ((S.words[s] as number) === 0) continue;
		const p = S.para[s] as number;
		const before = tokens.length;
		for (let t = S.tokStart[s] as number; t < (S.tokEnd[s] as number); t++) {
			if (T.cls[t] !== TOK_WORD) continue;
			const f = T.fold(T.id[t] as number);
			if (!FN.has(f)) tokens.push(f);
		}
		if (tokens.length === before) continue;
		if (cur === null || cur.p !== p) {
			cur = {
				p,
				from: before,
				to: tokens.length,
				charStart: S.charStart[s] as number,
				charEnd: S.charEnd[s] as number,
			};
			paragraphs.push(cur);
		} else {
			cur.to = tokens.length;
			cur.charEnd = S.charEnd[s] as number;
		}
	}
	return { tokens, paragraphs };
}

function run(ctx: RunContext): ChannelResult {
	const def = lx04Def;
	const gate = languageGate(def, ctx);
	if (gate.abstain) return abstainResult(def, gate.abstain);
	const notes: string[] = [];
	if (gate.note) notes.push(gate.note);
	const { doc } = ctx;
	if (doc.proseWords < LX04_MIN_PROSE_WORDS) return abstainResult(def, LX04_REASON_SHORT, notes);
	if (doc.proseWords >= LX04_MAX_PROSE_WORDS) return abstainResult(def, LX04_REASON_LONG, notes);

	const { tokens, paragraphs } = contentTokens(doc);
	const E = tokens.length;
	if (E < MATTR_WINDOW) return abstainResult(def, LX04_REASON_TOKENS, notes);
	const m = mattr(tokens);
	const value = m.value as number;

	// local MATTR of every paragraph with a full window of its own; the three highest are the evidence spans
	const local: Array<{ para: ContentParagraph; value: number }> = [];
	for (const para of paragraphs) {
		if (para.to - para.from < LX04_MIN_LOCAL_TOKENS) continue;
		const lm = mattr(tokens.slice(para.from, para.to));
		if (lm.value !== null) local.push({ para, value: lm.value });
	}
	const top = [...local].sort((a, b) => b.value - a.value || a.para.p - b.para.p).slice(0, 3);
	const ev = makeEvidence();
	for (const t of top) ev.add(t.para.charStart, t.para.charEnd, 0, t.value);
	const summary = [`MATTR-50 ${fmt(value, 3)} over ${E} content tokens`];
	for (const t of top) summary.push(`paragraph ${t.para.p + 1}: local MATTR-50 ${fmt(t.value, 3)}`);

	const result = scoredResult(def, {
		K: E,
		E,
		raw: value,
		estimate: value,
		deviation: 0,
		phi: 1,
		kappa: E / (E + LX04_KAPPA_M),
		notes,
		evidence: ev.build(),
		features: {
			mattr50: value,
			contentTokens: E,
			localParagraphs: local.length,
			maxLocalMattr: top.length ? (top[0] as { value: number }).value : 0,
		},
		summary,
	});
	// a level has no FDR test: the local MATTR is evidence only, so the hotspot flags are withheld (as under ED-13)
	result.flagsWithheld = LX04_FLAGS_WITHHELD;
	return result;
}

export const lx04: ChannelImpl = { def: lx04Def, phase: 'derived', run };
export { lx04Def as def, lx04 as impl };
